import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DisplayDataPaper from "./_displayDataPaper";
import PaginationData from "./PaginationData";
import DisplayDataTable from "./_displayDataTable";
import { ReactComponent as FilmSimple } from "../../assets/jobIcons/film-simple.svg";
import {
  TransactionHistoryProps,
  TransactionProps,
} from "./wallet/BillingOverview/types";
import ByteChip from "../icons/ByteChip";
import DollarIcon from "../icons/DollarIcon";
import { ArrowDownFromLine } from "lucide-react";
import exportFromJSON from "export-from-json";

const TransactionHistory = (props: TransactionHistoryProps) => {
  const {
    data,
    pagination,
    transactionHistoryFilterChange,
    addOrderFilterTransaction,
    handleExportCsv,
  } = props;
  const [currentPageSize, setCurrentPageSize] = useState<number>(5);
  const columnName = [
    "Date & Time",
    "Description",
    "Amount",
    "Currency Amount",
    "Balance",
  ];
  const columnOrder = [
    "createdAt",
    "description",
    "amount",
    "currencyAmount",
    "balance",
  ];
  const [tableData, setTableData] = useState<TransactionProps[]>([]);

  useEffect(() => {
    setTableData(data);
  }, [data]);
  const handlePageNumberClick = (page: number) => {
    const offset = (page - 1) * currentPageSize;
    transactionHistoryFilterChange(
      (prev: { limit: number; offset: number }) => ({
        ...prev,
        offset: offset,
      })
    );
  };

  const parsedTransactionHistoryData = (data: any[]) => {
    const parsedTransaction = data?.map((d) => ({
      createdAt: formateDateParameter(d.createdAt),
      description: d.description,
      amount: amountStatus(d.amount, d.transactionType),
      currencyAmount: currencyAmountView(
        d?.transactionType === "top_up" && d?.relatedEntityData?.currencyAmount
      ),
      balance: balanceStatus(d.balance),
    }));

    return parsedTransaction;
  };

  const amountStatus = (amount: string, status: string) => {
    const negativeMarking = status === "payment" ? true : false;
    return (
      <Stack direction={"row"} alignItems={"center"} columnGap={"1px"}>
        {negativeMarking && <Typography>-</Typography>}
        <ByteChip
          color={negativeMarking ? "textPrimary" : "success"}
          colorVariant="main"
          size="small"
        />
        <Typography
          sx={{
            fontWeight: 400,
            color: negativeMarking ? "#414040" : "#059B00",
          }}
        >
          {amount}
        </Typography>
      </Stack>
    );
  };
  const balanceStatus = (amount: string) => {
    return (
      amount && (
        <Stack direction={"row"} alignItems={"center"} columnGap={"1px"}>
          <ByteChip color={"textPrimary"} colorVariant="main" size="small" />
          <Typography
            sx={{
              fontWeight: 400,
              color: "#414040",
            }}
          >
            {amount}
          </Typography>
        </Stack>
      )
    );
  };
  const currencyAmountView = (amount: string) => {
    const convertedAmount = parseInt(amount, 10) / 100;
    return (
      amount && (
        <Stack direction={"row"} columnGap={"2px"} alignItems={"center"}>
          <DollarIcon color="textPrimary" colorVariant="main" size="xs" />
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              pt: "4px",
            }}
          >
            {convertedAmount}
          </Typography>
        </Stack>
      )
    );
  };
  const formateDateParameter = (date: string) => {
    const dateAndTime = new Date(date);
    const hours = dateAndTime.getHours() % 12 || 12;
    const minutes = String(dateAndTime.getUTCMinutes()).padStart(2, "0");
    const month = dateAndTime.toLocaleString("en-US", { month: "short" });
    const day = dateAndTime.getDate();
    const pmAm = hours >= 12 ? "PM" : "AM";
    const yearTarget = dateAndTime.getFullYear();
    if (yearTarget === new Date().getFullYear()) {
      return `${month} ${day}, ${hours}:${minutes} ${pmAm}`;
    } else {
      return `${month} ${day},${yearTarget},${hours}:${minutes} ${pmAm}`;
    }
  };

  const DisplayData = (
    data: any[],
    columnName: string[],
    columnOrder: string[],
    columnSortable: string[],
    onColumnClick: any
  ) => {
    return (
      <DisplayDataTable
        data={data}
        columnsNames={columnName}
        columnsOrder={columnOrder}
        currentPageSize={currentPageSize}
        columnsSortable={columnSortable}
        iconNoData={<FilmSimple />}
        onColumnClick={onColumnClick}
        messageNoData="Nothing in the transaction history so far"
      />
    );
  };
  const pageSize = [5, 10, 20, 50, 100];
  const onPageSizeChange = (e: SelectChangeEvent<number>) => {
    setCurrentPageSize(e.target.value as number);
  };
  useEffect(() => {
    transactionHistoryFilterChange(
      (prev: { limit: number; offset: number }) => ({
        ...prev,
        limit: currentPageSize,
      })
    );
  }, [currentPageSize]);
  const columnSortableQueue = [
    "createdAt",
    "description",
    "amount",
    "currencyAmount",
    "balance",
  ];
  const handleDownloadCurrentCsv = () => {
    const fileName = "transaction";
    const flattenObject = (obj: any, prefix = "") => {
      return Object.keys(obj).reduce((acc, key) => {
        const newKey = prefix ? `${prefix}_${key}` : key;

        if (typeof obj[key] === "object" && obj[key] !== null) {
          Object.assign(acc, flattenObject(obj[key], newKey));
        } else {
          acc[newKey] = obj[key];
        }

        return acc;
      }, {} as Record<string, any>);
    };

    const customizedData = tableData.map((row) => flattenObject(row));
    const exportType = exportFromJSON.types.csv;
    exportFromJSON({ data: customizedData, fileName, exportType });
  };

  return (
    <Box>
      <DisplayDataPaper
        title={"Transaction History"}
        children={DisplayData(
          parsedTransactionHistoryData(tableData),
          columnName,
          columnOrder,
          columnSortableQueue,
          addOrderFilterTransaction
        )}
        headerActions={
          <Stack direction={"row"} alignItems={"center"} columnGap={1}>
            <Typography>Show</Typography>
            <Select
              value={currentPageSize}
              onChange={onPageSizeChange}
              sx={{
                pt: "10px",
              }}
            >
              {pageSize.map((v, i) => (
                <MenuItem key={i} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        }
      />
      <PaginationData
        limit={currentPageSize}
        pagination={pagination}
        onPageNumberClick={handlePageNumberClick}
        exportOption={[
          {
            label: "Current View",
            onFunction: handleDownloadCurrentCsv,
            icon: <ArrowDownFromLine size={15} color="#B027CE" />,
          },
          {
            label: "All transactions",
            onFunction: handleExportCsv,
            icon: <ArrowDownFromLine size={15} color="#B027CE" />,
          },
        ]}
      />
    </Box>
  );
};

export default TransactionHistory;
